<script lang="ts">
  import * as _ from "lodash";
  import Icon from "@shared/svelte/Components/Icon.svelte";
  import FormatMoney from "@shared/svelte/Components/FormatMoney.svelte";
  import CartRipsDealDescription from "@shared/svelte/Components/Deal/CartRipsDealDescription.svelte";
  import UIRouterLink from "@shared/svelte/Components/UIRouterLink.svelte";
  import { discountUnitLabel } from "@retailer/svelte/Shared/DealHelpers.js";
  import { stagedRolloutCheck } from "@shared/svelte/Modules/StagedRolloutCheck.svelte";
  import StateSettingsStore from "@shared/svelte/Stores/StateSettingsStore";
  import { get as getStoreValue } from "svelte/store";
  import type { CartProduct } from "@shared/types/ProductLine";
  import type { DistributorCart } from "@shared/types/Distributor";
  import { pluralize } from "@shared/svelte/Modules/pluralize";
  import { DISCOUNT_AMOUNT_TYPES } from "@shared/types/Deals";

  export let total: number;
  export let subtotal: number;
  export let taxTotal: number;
  export let products: CartProduct[];
  export let orderProducts: CartProduct[];
  export let distributor: DistributorCart;

  const hasPricingLanguageRollout = stagedRolloutCheck("pricing_language");
  const stateSettings = getStoreValue(StateSettingsStore);
  const isRetailerApp = window["C"].APP_NAME === "retailer";

  let mixAndMatchDeals = [];
  let quantityDiscountProducts: CartProduct[] = [];
  let totalSavings = 0;
  let hasDiscounts = false;
  let unknownSavings = true;
  let priceUnknownText =
    "The final invoice from your distributor will reflect the total for this order.";

  const isUnknown = (input: any) => {
    return input == null || input == undefined || input == 0 || isNaN(input);
  };

  if (!products && orderProducts) {
    products = orderProducts;
  }

  const runDiscountCalculations = () => {
    const mixAndMatchDiscountsAndAmounts = [];

    products.forEach(orderProduct => {
      orderProduct.discounts.forEach(orderProductDiscount => {
        if (orderProductDiscount.just_an_upsell) return;

        const { discount } = orderProductDiscount;
        if (
          discount &&
          discount.deal_tier_id &&
          discount.amount_type !== DISCOUNT_AMOUNT_TYPES.RIP
        ) {
          mixAndMatchDiscountsAndAmounts.push({
            discount,
            discountAmount: orderProduct.discount_amount,
          });
        }
      });
    });

    let dealGroups = _.groupBy(
      mixAndMatchDiscountsAndAmounts,
      ({ discount }) => discount.deal_tier_id,
    );

    mixAndMatchDeals = Object.values(dealGroups)
      .map(discountsAndAmounts => {
        const { discount } = discountsAndAmounts[0];

        return {
          discount,
          total: discountsAndAmounts.reduce(
            (sum: number, { discountAmount }) => sum + discountAmount,
            0,
          ),
        };
      })
      .filter(n => n.discount && n.total > 0);

    quantityDiscountProducts = products.filter(
      op =>
        op.discounts &&
        op.discounts.some(d => d.discount?.id && !d.discount?.deal_tier_id && !d.just_an_upsell),
    );

    totalSavings = products.reduce(
      (sum: number, cartProduct: CartProduct) => sum + cartProduct.discount_amount,
      0,
    );
    hasDiscounts = mixAndMatchDeals.length > 0 || quantityDiscountProducts.length > 0;
    unknownSavings = isUnknown(subtotal);
  };

  $: {
    total = total;
    subtotal = subtotal;
    taxTotal = taxTotal;
    products = products;
    orderProducts = orderProducts;
    distributor = distributor;
    runDiscountCalculations();
  }
</script>

<section class="fizz-flex-row-0-responsive fizz-flex-vertical fizz-full-width">
  {#if hasDiscounts && !stateSettings["uses_future_pricing_with_min_bottle"]}
    <table class="fizz-full-width fizz-text-medium fizz-table-compact">
      {#each mixAndMatchDeals as { discount, total }}
        {#if total > 0}
          <tr class="cart__deal-tier-discount" deal-tier-id={discount.deal_tier_id}>
            <td class="align-left">
              {#if isRetailerApp}
                {#if discount.product_group_id}
                  <UIRouterLink
                    stateName="layout.deals_v2"
                    params={{
                      distributorId: distributor.id,
                      productGroupId: discount.product_group_id,
                    }}
                    trackingParams={{
                      discountClick: true,
                      distributor_id: distributor.id,
                      product_group_id: discount.product_group_id,
                    }}
                  >
                    {discount.deal_tier_name} &ndash; {discount.case_count}
                    {discount.deal_tier_label} mix deal
                  </UIRouterLink>
                {:else}
                  <UIRouterLink
                    stateName="layout.deal_detail"
                    classes="color-black"
                    params={{ dealId: discount.deal_id, dealTierId: discount.deal_tier_id }}
                    trackingParams={{
                      discountClick: true,
                      product_id: null,
                      distributor_id: distributor.id,
                      deal_tier_id: discount.deal_tier_id,
                    }}
                  >
                    <u
                      >{discount.deal_tier_name} &ndash; {discount.case_count}
                      {discount.deal_tier_label} mix deal</u
                    >
                  </UIRouterLink>
                {/if}
              {/if}
            </td>
            <td class="fizz-align-right">
              <FormatMoney input={-1 * total} />
            </td>
          </tr>
        {/if}
      {/each}
      {#each quantityDiscountProducts as cartProduct}
        {#if cartProduct && cartProduct.discount_amount > 0}
          {#each cartProduct.discounts.filter(d => !(d && d.discount && d.discount.deal_tier_id) && !d.just_an_upsell) as discount}
            <tr class="cart__deal-tier-discount">
              <td class="align-left">
                {cartProduct.product_line.name}
                {discount && discount.discount && discount.discount.case_count
                  ? discount.discount.case_count
                  : ""}
                {_.capitalize(discountUnitLabel(discount.discount, cartProduct.product))} QTY deal
              </td>
              <td class="fizz-align-right">
                <FormatMoney input={-1 * cartProduct.discount_amount} />
              </td>
            </tr>
          {/each}
        {/if}
      {/each}
      <tr class="cart__distributor-total-savings" distributor-id={distributor.id}>
        <td>Total Savings</td>
        <td class="fizz-align-right">
          <strong class="fizz-text-deal">
            <FormatMoney input={totalSavings} />
          </strong>
        </td>
      </tr>
    </table>
  {/if}
  <div data-testid="est-total-section" class="fizz-flex-row fizz-space-between fizz-full-width">
    <h4 class="fizz-heading-4">
      Est. Subtotal ({pluralize("item", products.length, true)})
    </h4>
    <div>
      {#if hasDiscounts && !unknownSavings && totalSavings > 0}
        <s>
          <FormatMoney input={subtotal} />
        </s>
      {/if}
      {#if hasPricingLanguageRollout && !total}
        <div class="fizz-tooltip-container">
          <button class="fizz-tooltip-trigger fizz-flex-row-8" aria-describedby="tooltip-pricing">
            <span>Applied on Invoice</span>
            <Icon type="Info" height="16" width="16" extraClassNames={"display-inline-block"} />
          </button>
          <div
            class="fizz-tooltip fizz-tooltip-right"
            role="tooltip"
            id="tooltip-pricing"
            style="white-space: break-spaces;"
          >
            {priceUnknownText}
          </div>
        </div>
      {:else}
        <strong class="fizz-text-large" id="total-price-section">
          <FormatMoney input={total} />
        </strong>
      {/if}
    </div>
  </div>
  {#if isRetailerApp && hasDiscounts && !stateSettings["uses_future_pricing_with_min_bottle"]}
    <p class="fizz-text-small fizz-text-deal" style="text-align: left;">
      <strong
        >*Some items are eligible for multiple deals - full discount will be reflected in your final
        invoice.</strong
      >
    </p>
  {/if}
  <CartRipsDealDescription
    {products}
    distributorId={distributor.id}
    distributorName={distributor.name}
  />
</section>
